import type {
  StatusBudget,
  versionInfo,
} from "../../../../../types/budget/budget";
import type { BudgetSummary } from "../../../../../types/budget/budget-summary";
import { filterServicesCountedInStageTotals } from "../../../../../utils/budget-stage-visibility";
import { getVisibleStageGrandTotal } from "../../../../../utils/visible-stage-totals";

/**
 * Narrow fields needed for a collapsed accordion row (D-01 Option A / FE-P1-06).
 * List/summary snapshots have no `budgetServices` and keep reading
 * `byItemType.SUM`. When the row is hydrated (activeBudget), the grand total
 * is rebuilt from cells that still have a visible service in this stage.
 */
export type CollapsedBudgetTotalsSource = Pick<
  BudgetSummary,
  | "budgetValuesTotal"
  | "negociationValuesTotal"
  | "closingValuesTotal"
  | "versionHistory"
> & {
  supplierList?: {
    supplierId?: string;
    calcOption?: string;
    deletedInVersions?: number[];
  }[];
  budgetServices?: {
    supplierId?: string;
    type?: string;
    calcOption?: string;
    versions?: number[];
    budgetValues?: { calcOption?: string; totals?: { totalInBRL?: number } };
    negotiationValue?: Array<
      { calcOption?: string; totals?: { totalInBRL?: number } } | undefined
    >;
    closingValues?: { calcOption?: string; totals?: { totalInBRL?: number } };
  }[];
  addedItemTypeList?: {
    type?: string;
    supplierId?: string;
    calcOption?: string;
    deletedInVersions?: number[];
  }[];
};

/**
 * Etapa cujos valores a linha da lista exibe. A linha recebe o status BRUTO do
 * `versionHistory`, mas o fechamento reprovado (`DENIED_CLOSING`) continua
 * sendo a etapa de fechamento — e a negociação reprovada (`DENIED`), a de
 * negociação. É a mesma leitura da barra inferior, que deriva a etapa da aba.
 * Sem isso o "Orçamento 1" reprovado no fechamento expandia com tudo R$ 0,00
 * (lia `negotiationValue`, vazio) enquanto a linha fechada mostrava o total.
 */
export function resolveSidebarStageStatus(
  status: StatusBudget | undefined,
  version: number
): StatusBudget | undefined {
  if (status === "DENIED_CLOSING") return "APPROVED" as StatusBudget;
  if (status === "DENIED" && version > 0) return "NEGOTIATE" as StatusBudget;
  return status;
}

/**
 * Valor do serviço na etapa. O bloco da etapa, quando existe, vale mesmo
 * zerado: `||` trocava o R$ 0,00 do fechamento pelo valor do orçamento e o
 * painel somava um serviço que o fechamento zerou (MMV-2626-2). O orçamento só
 * entra quando o serviço nem tem o bloco da etapa (legado).
 */
function serviceStageTotalInBRL(
  service: {
    budgetValues?: { totals?: { totalInBRL?: number } };
    negotiationValue?: Array<{ totals?: { totalInBRL?: number } } | undefined>;
    closingValues?: { totals?: { totalInBRL?: number } };
  },
  status: StatusBudget | undefined,
  version: number
): number {
  const budgetTotal = service.budgetValues?.totals?.totalInBRL || 0;
  if (status === "APPROVED") {
    return service.closingValues
      ? service.closingValues.totals?.totalInBRL ?? 0
      : budgetTotal;
  }
  if (status === "NEGOTIATE" && version > 0) {
    const negotiation = service.negotiationValue?.[version - 1];
    return negotiation ? negotiation.totals?.totalInBRL ?? 0 : budgetTotal;
  }
  return budgetTotal;
}

function sumTotalInBRL(
  totals:
    | CollapsedBudgetTotalsSource["budgetValuesTotal"]
    | CollapsedBudgetTotalsSource["closingValuesTotal"]
    | CollapsedBudgetTotalsSource["negociationValuesTotal"][number]
    | undefined
): number {
  return totals?.byItemType?.SUM?.totalInBRL || 0;
}

/**
 * Collapsed sidebar total for Orçamento / Negociação / Fechamento tabs.
 * Summary-only rows still read `byItemType.SUM`. Hydrated rows drop leftover
 * cells (RSVP em C sem serviço, `|dup|` sem linha na etapa).
 */
export function getCollapsedBudgetTotal(
  budget: CollapsedBudgetTotalsSource,
  rawStatus: StatusBudget | undefined,
  version: number
): number {
  const status = resolveSidebarStageStatus(rawStatus, version);
  if (
    Array.isArray(budget.budgetServices) &&
    budget.budgetServices.length > 0
  ) {
    return filterServicesCountedInStageTotals(
      budget.budgetServices as never,
      version,
      status,
      budget.supplierList,
      budget.addedItemTypeList
    ).reduce(
      (acc, service) =>
        acc + serviceStageTotalInBRL(service as never, status, version),
      0
    );
  }
  return getVisibleStageGrandTotal(budget, status, version);
}

function findPreviousValidVersion(
  versionHistory: versionInfo[] | undefined,
  givenVersion: number
): versionInfo | null {
  const validVersions =
    versionHistory?.filter(
      (v) => v.status !== "DELETED" && v.status !== "DENIED"
    ) || [];
  const versionInfoEntry = versionHistory?.find(
    (el) => el.version === givenVersion
  );
  if (versionInfoEntry) {
    return (
      versionHistory?.find(
        (el) => el.version === versionInfoEntry.comparisonVersion
      ) || null
    );
  }
  const previousVersion = [...validVersions]
    .filter((v) => v.version < givenVersion)
    .sort((a, b) => b.version - a.version)[0];
  return previousVersion || null;
}

/**
 * Delta vs comparison / previous version for collapsed savings display.
 * Derived entirely from totals snapshots + `versionHistory` — no services.
 */
export function getCollapsedBudgetSavings(
  budget: CollapsedBudgetTotalsSource,
  status: StatusBudget | undefined,
  version: number
): number {
  const totals = getCollapsedBudgetTotal(budget, status, version);
  const currentVersionInfo = budget.versionHistory?.find(
    (el) => el.version === version
  );
  const earlierVersionInfo = findPreviousValidVersion(
    budget.versionHistory,
    version
  );
  const earlierVersion = earlierVersionInfo?.version;
  const totalLastVersion = !earlierVersion
    ? sumTotalInBRL(budget.budgetValuesTotal)
    : currentVersionInfo?.comparisonVersion === 0
    ? sumTotalInBRL(budget.budgetValuesTotal)
    : sumTotalInBRL(budget.negociationValuesTotal?.[earlierVersion - 1]);
  return totals - totalLastVersion;
}
